import React, { useState } from 'react';
import {
  ActivityWithTasks,
  ActivityTask,
  JobPositionLegalFields,
  createTask,
  updateTask,
  deleteTask,
  reorderTasks,
  updateJobPositionLegalFields,
} from '../lib/gestion-tecnica-constructor';
import TaskHazardsModal from './TaskHazardsModal';

interface Props {
  companyId: string;
  uploadedBy: string | null;
  activity: ActivityWithTasks;
  position: JobPositionLegalFields | null;
  processName: string;
  onClose: () => void;
  onChanged: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

export default function ActivityDetailModal({ companyId, uploadedBy, activity, position, processName, onClose, onChanged }: Props) {
  const [editingPosition, setEditingPosition] = useState(false);
  const [numeroTrabajadores, setNumeroTrabajadores] = useState(
    position?.numero_trabajadores != null ? String(position.numero_trabajadores) : ''
  );
  const [actividadesRealizadas, setActividadesRealizadas] = useState(position?.actividades_realizadas ?? '');
  const [horasActividad, setHorasActividad] = useState(
    position?.horas_actividad_diarias != null ? String(position.horas_actividad_diarias) : ''
  );
  const [recursosUtilizados, setRecursosUtilizados] = useState(position?.recursos_utilizados ?? '');
  const [materialesAgentes, setMaterialesAgentes] = useState(position?.materiales_agentes ?? '');
  const [savingPosition, setSavingPosition] = useState(false);

  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDuration, setNewTaskDuration] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [taskNameError, setTaskNameError] = useState('');
  const [hazardsTaskId, setHazardsTaskId] = useState<string | null>(null);

  async function handleSavePosition() {
    if (!position) return;
    setSavingPosition(true);
    try {
      await updateJobPositionLegalFields(position.id, {
        numero_trabajadores: numeroTrabajadores.trim() === '' ? null : Number(numeroTrabajadores),
        actividades_realizadas: actividadesRealizadas.trim() || null,
        horas_actividad_diarias: horasActividad.trim() === '' ? null : Number(horasActividad),
        recursos_utilizados: recursosUtilizados.trim() || null,
        materiales_agentes: materialesAgentes.trim() || null,
      });
      setEditingPosition(false);
      onChanged();
    } finally {
      setSavingPosition(false);
    }
  }

  async function handleAddTask() {
    if (!newTaskName.trim()) {
      setTaskNameError('Escribe un nombre para la tarea.');
      return;
    }
    setTaskNameError('');
    setSavingTask(true);
    try {
      await createTask(
        companyId,
        activity.id,
        newTaskName.trim(),
        newTaskDuration.trim() === '' ? null : Number(newTaskDuration)
      );
      setNewTaskName('');
      setNewTaskDuration('');
      onChanged();
    } finally {
      setSavingTask(false);
    }
  }

  async function handleRenameTask(id: string, name: string) {
    await updateTask(id, { name });
    onChanged();
  }

  async function handleDurationTask(id: string, duration: string) {
    await updateTask(id, { duration_minutes: duration.trim() === '' ? null : Number(duration) });
    onChanged();
  }

  async function handleDeleteTask(id: string) {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    await deleteTask(id);
    onChanged();
  }

  async function handleMoveTask(tasks: ActivityTask[], id: string, direction: -1 | 1) {
    const idx = tasks.findIndex(t => t.id === id);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= tasks.length) return;
    const ordered = [...tasks];
    [ordered[idx], ordered[swapIdx]] = [ordered[swapIdx], ordered[idx]];
    await reorderTasks(ordered.map(t => t.id));
    onChanged();
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', zIndex: 400, padding: '24px 16px', overflowY: 'auto',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
        width: '100%', maxWidth: 640, boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
        marginTop: 'auto', marginBottom: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' }}>
          <h2 style={{ margin: 0 }}>{activity.name}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#e2e8f0', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <h3 style={{ marginTop: 0 }}>Puesto asignado (GT-02)</h3>
          {!position && (
            <p style={{ color: '#94a3b8' }}>
              Esta actividad no tiene puesto asignado. Selecciona uno desde el
              flujo de actividades para completar el descriptivo de puesto.
            </p>
          )}
          {position && !editingPosition && (
            <div>
              <p><strong>{position.name}</strong></p>
              <button onClick={() => setEditingPosition(true)} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
                Editar datos del puesto
              </button>
            </div>
          )}
          {position && editingPosition && (
            <div>
              <div style={{ background: '#78350f', color: '#fde68a', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                ⚠️ Estos datos pertenecen al puesto "{position.name}" y se
                comparten con todas las actividades donde participa este mismo
                puesto — no son exclusivos de "{activity.name}".
              </div>
              <label>Número de trabajadores</label>
              <input type="number" min="0" step="1" value={numeroTrabajadores} onChange={e => setNumeroTrabajadores(e.target.value)} style={inputStyle} />
              <label style={{ display: 'block', marginTop: 12 }}>Actividades realizadas</label>
              <textarea rows={2} value={actividadesRealizadas} onChange={e => setActividadesRealizadas(e.target.value)} style={inputStyle} />
              <label style={{ display: 'block', marginTop: 12 }}>Horas de jornada laboral</label>
              <input type="number" min="0" step="0.5" value={horasActividad} onChange={e => setHorasActividad(e.target.value)} style={inputStyle} />
              <label style={{ display: 'block', marginTop: 12 }}>Recursos utilizados</label>
              <textarea rows={2} value={recursosUtilizados} onChange={e => setRecursosUtilizados(e.target.value)} style={inputStyle} />
              <label style={{ display: 'block', marginTop: 12 }}>Materiales y agentes</label>
              <textarea rows={2} value={materialesAgentes} onChange={e => setMaterialesAgentes(e.target.value)} style={inputStyle} />
              <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                <button disabled={savingPosition} onClick={handleSavePosition} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
                  {savingPosition ? 'Guardando...' : 'Guardar'}
                </button>
                <button onClick={() => setEditingPosition(false)} style={{ background: 'transparent', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          <h3 style={{ marginTop: 28 }}>Tareas</h3>
          {activity.activity_tasks.length === 0 && <p style={{ color: '#94a3b8' }}>Sin tareas todavía.</p>}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {activity.activity_tasks.map((t, idx) => (
              <li key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #334155' }}>
                <input
                  type="text"
                  defaultValue={t.name}
                  onBlur={e => handleRenameTask(t.id, e.target.value)}
                  style={{ ...inputStyle, marginTop: 0, flex: 1 }}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="min"
                  defaultValue={t.duration_minutes ?? ''}
                  onBlur={e => handleDurationTask(t.id, e.target.value)}
                  style={{ ...inputStyle, marginTop: 0, width: 70 }}
                />
                <button disabled={idx === 0} onClick={() => handleMoveTask(activity.activity_tasks, t.id, -1)}>↑</button>
                <button disabled={idx === activity.activity_tasks.length - 1} onClick={() => handleMoveTask(activity.activity_tasks, t.id, 1)}>↓</button>
                <button onClick={() => setHazardsTaskId(t.id)}>Peligros</button>
                <button onClick={() => handleDeleteTask(t.id)}>🗑</button>
              </li>
            ))}
          </ul>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <input type="text" placeholder="Nombre de la tarea" value={newTaskName} onChange={e => { setNewTaskName(e.target.value); setTaskNameError(''); }} style={{ ...inputStyle, marginTop: 0, flex: 1 }} />
            <input type="number" min="0" placeholder="min" value={newTaskDuration} onChange={e => setNewTaskDuration(e.target.value)} style={{ ...inputStyle, marginTop: 0, width: 70 }} />
            <button disabled={savingTask} onClick={handleAddTask} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
              + Tarea
            </button>
          </div>
          {taskNameError && <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>{taskNameError}</span>}

          <p style={{ color: '#64748b', fontSize: 12, marginTop: 20 }}>
            Usa el botón "Peligros" en cada tarea para identificar peligros y
            completar la evaluación GTC-45 (GT-04).
          </p>
        </div>
      </div>

      {hazardsTaskId && (() => {
        const t = activity.activity_tasks.find(x => x.id === hazardsTaskId);
        if (!t) return null;
        return (
          <TaskHazardsModal
            companyId={companyId}
            positionId={activity.position_id}
            processName={processName}
            activityName={activity.name}
            task={t}
            onClose={() => setHazardsTaskId(null)}
          />
        );
      })()}
    </div>
  );
}
